import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useGallery,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';
import { HeroActionRail, railScheduleDef } from './HeroActionRail';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function hasBlurbContent(value: string | null | undefined): boolean {
  return Boolean(stripHtml(value || '').trim());
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const bgPath = `${prefix}.bg_image`;

  const blurbContent = useContentToken(blurbPath, {
    defaultField: blurbField,
    defaultValue: defaultText || '',
  });
  const showBlurb = hasBlurbContent(blurbContent);
  const bgImage = useSlotGroup(bgPath, bgImageDef);
  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');

  const pageHint = prefix;
  const keywords = useMemo(
    () => title.toLowerCase().split(/\s+/).filter(Boolean),
    [title],
  );

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );

  const displayImage =
    bgImage.image ||
    (photoIndex >= 0 ? gallery[photoIndex]?.image : null) ||
    featuredImage ||
    gallery[0]?.image ||
    null;

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="hero-solid-bg">
      {/* Photographic hero — title centered over featured image */}
      <div className="hero-solid-hero hero-rail-anchor relative w-full overflow-hidden">
        <EditableSlotGroup groupPath={bgPath} definition={bgImageDef} className="absolute inset-0">
          {displayImage ? (
            <VibesitesImage
              url={displayImage}
              alt={bgImage.alt || title}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-[var(--vibe-color-text)]" />
          )}
        </EditableSlotGroup>

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

        <div className="hero-solid-title-wrap relative z-10 flex h-full flex-col items-center justify-center px-6 sm:px-8 text-center text-white">
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hero-solid-title"
          />
        </div>

        <HeroActionRail variant="schedule-only" />
      </div>

      {showBlurb ? (
        <div className="hero-solid-blurb bg-white text-[var(--vibe-color-text)]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 text-center">
            <RichTextContent
              path={blurbPath}
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="hero-solid-blurb-text font-body text-base md:text-lg leading-relaxed [&_a]:underline [&_a]:text-[var(--vibe-color-primary)]"
            />
          </div>
        </div>
      ) : null}

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="hero.rail_schedule"
        definition={railScheduleDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .hero-solid-hero {
          /* Rail tile size matches the home hero (1/5 viewport), not this shorter hero. */
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          height: 50vh;
          height: 50svh;
          height: 50dvh;
          max-height: 50vh;
          max-height: 50svh;
          max-height: 50dvh;
          min-height: 280px;
        }
        @supports (height: 100dvh) {
          .hero-solid-hero {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }
        /* The header is fixed and transparent over this hero. On short mobile
           viewports the vertically-centered title can slide behind it, so pad
           the centering region down past the header (plus any offer banner). */
        @media (max-width: 767px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + 5.5rem);
          }
        }
        .hero-solid-title {
          font-family: "Raleway", sans-serif;
          font-size: 42px;
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0;
          max-width: 20ch;
        }
        .hero-solid-title p,
        .hero-solid-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-title { font-size: 64px; }
        }
        @media (min-width: 1024px) {
          .hero-solid-title { font-size: 72px; }
        }
        .hero-solid-blurb-text p {
          margin: 0;
        }
        .hero-solid-blurb-text p + p {
          margin-top: 1rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'hero.rail_schedule': {
      label: 'Rail · Schedule a Visit',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
